import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import { ConsentStatus, ConsentType, FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { firstValueFrom } from 'rxjs';
import { baseUrl, environment } from 'src/environments/environment';

export type AnalyticsConsentStatus = 'granted' | 'denied';

export interface AnalyticsConsentPreference {
  analytics: AnalyticsConsentStatus;
  ads: AnalyticsConsentStatus;
}

export type AnalyticsEventName =
  | 'sign_up'
  | 'login'
  | 'view_promotion'
  | 'select_promotion'
  | 'select_content'
  | 'join_group'
  | 'view_item_list'
  | 'select_item'
  | 'begin_checkout'
  | 'purchase';

export interface AnalyticsItem {
  item_id?: string;
  item_name?: string;
  item_category?: string;
  item_brand?: string;
  price?: number;
  quantity?: number;
}

const MOBILE_CONSENT_STORAGE_KEY = 'aio_consent_mobile_app';
const WEB_CONSENT_COOKIE_KEY = 'aio_consent';
const PENDING_CHECKOUT_STORAGE_PREFIX = 'aio_pending_checkout_';
const PURCHASED_TRANSACTION_STORAGE_PREFIX = 'aio_purchase_logged_';
const ALLOWED_ANALYTICS_EVENTS = new Set<AnalyticsEventName>([
  'sign_up',
  'login',
  'view_promotion',
  'select_promotion',
  'select_content',
  'join_group',
  'view_item_list',
  'select_item',
  'begin_checkout',
  'purchase',
]);

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private currentUserEmail = '';

  constructor(private http: HttpClient) { }

  setCurrentUserEmail(email: string): void {
    this.currentUserEmail = String(email || '').trim().toLowerCase();
  }

  async setCurrentScreen(screenName: string): Promise<void> {
    await this.runSafely(() => FirebaseAnalytics.setCurrentScreen({
      screenName,
      screenClassOverride: screenName,
    }));
  }

  async setUser(user: any): Promise<void> {
    const userId = user?.uid ?? user?.user_id ?? user?.id ?? null;
    this.currentUserEmail = String(user?.email || '').trim().toLowerCase();

    await this.runSafely(() => FirebaseAnalytics.setUserId({
      userId: userId === null || userId === undefined ? null : String(userId),
    }));

    const contactId = user?.contactId ?? user?.contact_id;
    if (contactId !== null && contactId !== undefined) {
      await this.setUserProperty('contact_id', String(contactId));
    }

    const defaultCompanyId = user?.default;
    if (defaultCompanyId !== null && defaultCompanyId !== undefined) {
      await this.setUserProperty('default_company_id', String(defaultCompanyId));
    }
  }

  async clearUser(): Promise<void> {
    this.currentUserEmail = '';
    await this.runSafely(() => FirebaseAnalytics.setUserId({ userId: null }));
    await this.setUserProperty('contact_id', null);
    await this.setUserProperty('default_company_id', null);
  }

  async logEvent(name: AnalyticsEventName, params?: Record<string, any>): Promise<void> {
    if (!ALLOWED_ANALYTICS_EVENTS.has(name)) {
      console.warn('[analytics] Blocked unknown analytics event:', name);
      return;
    }

    if (!this.canLogAnalyticsEvent()) {
      return;
    }

    await this.runSafely(() => FirebaseAnalytics.logEvent({
      name,
      params: this.sanitizeEventParams(params || {}),
    }));
  }

  async trackLogin(method = 'password'): Promise<void> {
    await this.logEvent('login', {
      method,
      platform: this.getAnalyticsPlatform(),
    });
  }

  async trackSignUp(method = 'email'): Promise<void> {
    await this.logEvent('sign_up', {
      method,
      platform: this.getAnalyticsPlatform(),
    });
  }

  async trackViewItemList(listName: string, items: AnalyticsItem[]): Promise<void> {
    await this.logEvent('view_item_list', {
      item_list_name: listName,
      items: this.normalizeItems(items),
    });
  }

  async trackSelectItem(item: AnalyticsItem, listName?: string): Promise<void> {
    await this.logEvent('select_item', {
      item_list_name: listName,
      items: this.normalizeItems([item]),
    });
  }

  async trackSelectContent(contentType: string, itemId: string, itemName?: string): Promise<void> {
    await this.logEvent('select_content', {
      content_type: contentType,
      item_id: itemId,
      item_name: itemName,
    });
  }

  async trackViewPromotion(promotion: {
    promotion_id?: string;
    promotion_name?: string;
    creative_name?: string;
    creative_slot?: string;
    location_id?: string;
    items?: AnalyticsItem[];
  }): Promise<void> {
    await this.logEvent('view_promotion', {
      promotion_id: promotion.promotion_id,
      promotion_name: promotion.promotion_name,
      creative_name: promotion.creative_name,
      creative_slot: promotion.creative_slot,
      location_id: promotion.location_id,
      items: this.normalizeItems(promotion.items || []),
    });
  }

  async trackSelectPromotion(promotion: {
    promotion_id?: string;
    promotion_name?: string;
    creative_name?: string;
    creative_slot?: string;
    location_id?: string;
    items?: AnalyticsItem[];
  }): Promise<void> {
    await this.logEvent('select_promotion', {
      promotion_id: promotion.promotion_id,
      promotion_name: promotion.promotion_name,
      creative_name: promotion.creative_name,
      creative_slot: promotion.creative_slot,
      location_id: promotion.location_id,
      items: this.normalizeItems(promotion.items || []),
    });
  }

  async trackBeginCheckout(data: {
    transaction_id?: string;
    currency?: string;
    value?: number;
    coupon?: string;
    payment_type?: string;
    payment_method?: string;
    items?: AnalyticsItem[];
  }): Promise<void> {
    if (data.transaction_id) {
      this.rememberPendingCheckout(data.transaction_id, data);
    }

    await this.logEvent('begin_checkout', {
      transaction_id: data.transaction_id,
      currency: this.normalizeCurrency(data.currency),
      value: this.normalizeNumber(data.value),
      coupon: data.coupon || undefined,
      payment_type: data.payment_type,
      payment_method: data.payment_method,
      items: this.normalizeItems(data.items || []),
    });
  }

  async trackPurchase(data: {
    transaction_id: string;
    currency?: string;
    value?: number;
    coupon?: string;
    payment_type?: string;
    payment_status?: string;
    items?: AnalyticsItem[];
  }): Promise<void> {
    await this.logEvent('purchase', {
      transaction_id: data.transaction_id,
      currency: this.normalizeCurrency(data.currency),
      value: this.normalizeNumber(data.value),
      coupon: data.coupon || undefined,
      payment_type: data.payment_type,
      payment_status: data.payment_status,
      items: this.normalizeItems(data.items || []),
    });
  }

  async trackPurchaseForTransaction(transactionId: string, paymentStatus = 'succeeded'): Promise<void> {
    const normalizedTransactionId = String(transactionId || '').trim();
    if (!normalizedTransactionId || normalizedTransactionId === 'null') {
      return;
    }

    if (this.hasLoggedPurchase(normalizedTransactionId)) {
      return;
    }

    const pendingCheckout = this.getPendingCheckout(normalizedTransactionId);
    await this.trackPurchase({
      transaction_id: normalizedTransactionId,
      currency: pendingCheckout?.currency,
      value: pendingCheckout?.value,
      coupon: pendingCheckout?.coupon,
      payment_type: pendingCheckout?.payment_type,
      payment_status: paymentStatus,
      items: pendingCheckout?.items || [],
    });
    this.markPurchaseLogged(normalizedTransactionId);
  }

  rememberPendingCheckout(transactionId: string, data: {
    currency?: string;
    value?: number;
    coupon?: string;
    payment_type?: string;
    payment_method?: string;
    items?: AnalyticsItem[];
  }): void {
    const normalizedTransactionId = String(transactionId || '').trim();
    if (!normalizedTransactionId || normalizedTransactionId === 'null' || typeof sessionStorage === 'undefined') {
      return;
    }

    sessionStorage.setItem(`${PENDING_CHECKOUT_STORAGE_PREFIX}${normalizedTransactionId}`, JSON.stringify({
      currency: this.normalizeCurrency(data.currency),
      value: this.normalizeNumber(data.value),
      coupon: data.coupon || '',
      payment_type: data.payment_type || '',
      payment_method: data.payment_method || '',
      items: this.normalizeItems(data.items || []),
    }));
  }

  async trackJoinGroup(groupId: string, groupName?: string): Promise<void> {
    await this.logEvent('join_group', {
      group_id: groupId,
      group_name: groupName,
    });
  }

  getStoredMobileConsentPreference(): AnalyticsConsentPreference | null {
    try {
      const value = localStorage.getItem(MOBILE_CONSENT_STORAGE_KEY);
      if (!value) {
        return null;
      }

      return this.normalizeConsentPreference(JSON.parse(value));
    } catch (error) {
      console.warn('[analytics] Failed to read stored consent preference:', error);
      return null;
    }
  }

  getStoredWebConsentPreference(): AnalyticsConsentPreference | null {
    const cookieValue = this.getCookie(WEB_CONSENT_COOKIE_KEY);
    if (!cookieValue) {
      return null;
    }

    try {
      return this.normalizeConsentPreference(JSON.parse(cookieValue));
    } catch (error) {
      console.warn('[analytics] Failed to read web consent cookie:', error);
      return null;
    }
  }

  getStoredConsentPreference(isNativePlatform: boolean): AnalyticsConsentPreference | null {
    return isNativePlatform ? this.getStoredMobileConsentPreference() : this.getStoredWebConsentPreference();
  }

  async saveAndApplyConsentPreference(preference: AnalyticsConsentPreference): Promise<void> {
    const normalizedPreference = this.normalizeConsentPreference(preference);
    localStorage.setItem(MOBILE_CONSENT_STORAGE_KEY, JSON.stringify(normalizedPreference));
    await this.applyConsentPreference(normalizedPreference);
    await this.syncConsentPreference(normalizedPreference);
  }

  async saveAndApplyPlatformConsentPreference(
    preference: AnalyticsConsentPreference,
    isNativePlatform: boolean,
  ): Promise<void> {
    const normalizedPreference = this.normalizeConsentPreference(preference);

    if (isNativePlatform) {
      localStorage.setItem(MOBILE_CONSENT_STORAGE_KEY, JSON.stringify(normalizedPreference));
    } else {
      this.saveWebConsentPreference(normalizedPreference);
    }

    await this.applyConsentPreference(normalizedPreference);
    await this.syncConsentPreference(normalizedPreference);
  }

  async applyConsentPreference(preference: AnalyticsConsentPreference): Promise<void> {
    const normalizedPreference = this.normalizeConsentPreference(preference);
    this.updateWebConsent(normalizedPreference);

    await Promise.all([
      this.setFirebaseConsent(ConsentType.AnalyticsStorage, normalizedPreference.analytics),
      this.setFirebaseConsent(ConsentType.AdStorage, normalizedPreference.ads),
      this.setFirebaseConsent(ConsentType.AdUserData, normalizedPreference.ads),
      this.setFirebaseConsent(ConsentType.AdPersonalization, normalizedPreference.ads),
    ]);
  }

  private async setUserProperty(key: string, value: string | null): Promise<void> {
    await this.runSafely(() => FirebaseAnalytics.setUserProperty({ key, value }));
  }

  private async setFirebaseConsent(type: ConsentType, status: AnalyticsConsentStatus): Promise<void> {
    await this.runSafely(() => FirebaseAnalytics.setConsent({
      type,
      status: status === 'granted' ? ConsentStatus.Granted : ConsentStatus.Denied,
    }));
  }

  private updateWebConsent(preference: AnalyticsConsentPreference): void {
    if (typeof window === 'undefined') {
      return;
    }

    const consentUpdate = {
      ad_storage: preference.ads,
      ad_user_data: preference.ads,
      ad_personalization: preference.ads,
      analytics_storage: preference.analytics,
    };

    if (typeof (window as any).gtag === 'function') {
      (window as any).gtag('consent', 'update', consentUpdate);
      return;
    }

    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push(['consent', 'update', consentUpdate]);
  }

  private saveWebConsentPreference(preference: AnalyticsConsentPreference): void {
    if (typeof document === 'undefined') {
      return;
    }

    const expiryDays = preference.analytics === 'granted' && preference.ads === 'granted' ? 180 : 1;
    const expires = new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000).toUTCString();
    const value = encodeURIComponent(JSON.stringify(preference));
    const domain = this.getCookieDomain();

    document.cookie = [
      `${WEB_CONSENT_COOKIE_KEY}=${value}`,
      `expires=${expires}`,
      'path=/',
      domain,
      'SameSite=Lax',
    ].filter(Boolean).join(';');
  }

  private getCookie(name: string): string | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const cookieName = `${name}=`;
    const cookies = decodeURIComponent(document.cookie || '').split(';');

    for (const cookie of cookies) {
      const trimmedCookie = cookie.trim();
      if (trimmedCookie.indexOf(cookieName) === 0) {
        return trimmedCookie.substring(cookieName.length);
      }
    }

    return null;
  }

  private getCookieDomain(): string {
    if (typeof window === 'undefined') {
      return '';
    }

    const hostname = window.location.hostname;
    return hostname === 'altomate.io' || hostname.endsWith('.altomate.io') ? 'domain=.altomate.io' : '';
  }

  private async syncConsentPreference(preference: AnalyticsConsentPreference): Promise<void> {
    await this.runSafely(async () => {
      await firstValueFrom(this.http.post(`${baseUrl}/analytics-consent`, {
        platform: this.getAnalyticsPlatform(),
        analytics: preference.analytics,
        ads: preference.ads,
        analytics_storage: preference.analytics,
        ad_storage: preference.ads,
        ad_user_data: preference.ads,
        ad_personalization: preference.ads,
      }));
    });
  }

  private getAnalyticsPlatform(): 'web' | 'android' | 'ios' | 'unknown' {
    const platform = Capacitor.getPlatform();
    return platform === 'web' || platform === 'android' || platform === 'ios' ? platform : 'unknown';
  }

  private canLogAnalyticsEvent(): boolean {
    if (this.isExcludedAnalyticsUser()) {
      return false;
    }

    const preference = this.getStoredConsentPreference(Capacitor.getPlatform() !== 'web');
    return preference?.analytics === 'granted';
  }

  private isExcludedAnalyticsUser(): boolean {
    if (!this.currentUserEmail) {
      return false;
    }

    const email = this.currentUserEmail;
    return email.includes('+test') ||
      email.includes('test@') ||
      email.endsWith('@example.com') ||
      email.endsWith('@test.com');
  }

  private sanitizeEventParams(params: Record<string, any>): Record<string, any> {
    const sanitized: Record<string, any> = {};

    for (const [key, value] of Object.entries(params || {})) {
      if (value === undefined || value === null || value === '') {
        continue;
      }

      if (Array.isArray(value)) {
        sanitized[key] = value;
        continue;
      }

      if (typeof value === 'number') {
        if (Number.isFinite(value)) {
          sanitized[key] = value;
        }
        continue;
      }

      if (typeof value === 'boolean') {
        sanitized[key] = value;
        continue;
      }

      sanitized[key] = String(value).slice(0, 100);
    }

    return sanitized;
  }

  private normalizeItems(items: AnalyticsItem[]): AnalyticsItem[] {
    return (items || [])
      .filter(Boolean)
      .slice(0, 20)
      .map((item, index) => ({
        item_id: String(item.item_id || item.item_name || `item_${index + 1}`).slice(0, 100),
        item_name: String(item.item_name || item.item_id || `Item ${index + 1}`).slice(0, 100),
        item_category: item.item_category ? String(item.item_category).slice(0, 100) : undefined,
        item_brand: item.item_brand ? String(item.item_brand).slice(0, 100) : 'Altomate',
        price: this.normalizeNumber(item.price),
        quantity: this.normalizeNumber(item.quantity) || 1,
      }));
  }

  private normalizeCurrency(value: any): string {
    const normalized = String(value || '').trim().toUpperCase();
    if (normalized === 'RM') return 'MYR';
    if (normalized === 'S$') return 'SGD';
    return normalized || 'MYR';
  }

  private normalizeNumber(value: any): number {
    const numberValue = Number(value || 0);
    return Number.isFinite(numberValue) ? Number(numberValue.toFixed(2)) : 0;
  }

  private getPendingCheckout(transactionId: string): any | null {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }

    try {
      const value = sessionStorage.getItem(`${PENDING_CHECKOUT_STORAGE_PREFIX}${transactionId}`);
      return value ? JSON.parse(value) : null;
    } catch (error) {
      console.warn('[analytics] Failed to read pending checkout:', error);
      return null;
    }
  }

  private hasLoggedPurchase(transactionId: string): boolean {
    return typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem(`${PURCHASED_TRANSACTION_STORAGE_PREFIX}${transactionId}`) === 'true';
  }

  private markPurchaseLogged(transactionId: string): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    sessionStorage.setItem(`${PURCHASED_TRANSACTION_STORAGE_PREFIX}${transactionId}`, 'true');
    sessionStorage.removeItem(`${PENDING_CHECKOUT_STORAGE_PREFIX}${transactionId}`);
  }

  private normalizeConsentPreference(preference: any): AnalyticsConsentPreference {
    return {
      analytics: preference?.analytics === 'granted' ? 'granted' : 'denied',
      ads: preference?.ads === 'granted' ? 'granted' : 'denied',
    };
  }

  private async runSafely(action: () => Promise<void>): Promise<void> {
    if (!environment.production) {
      return;
    }

    try {
      await action();
    } catch (error) {
      console.warn('[analytics] Firebase Analytics call failed:', error);
    }
  }
}
